import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BellIcon, ClockIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../components/LoadingSpinner';
import SEO from '../components/SEO';
import MatchReasons from '../components/MatchReasons';
import { notificationAPI } from '../services/notificationAPI';
import type { NotificationItem } from '../services/notificationAPI';
import { useNotifications } from '../hooks/useNotifications';
import { DEFAULT_REGION, buildRegionPath, useActiveRegion } from '../regions';
import { notificationAge } from '../utils/notificationAge';

const inactiveLabels: Record<string, string> = {
  opportunity_unsaved: 'Opportunity no longer saved',
  opportunity_closed: 'Opportunity closed',
  opportunity_removed: 'Opportunity no longer available',
  deadline_changed: 'Deadline changed',
};

const Notifications = () => {
  const { refreshCount } = useNotifications();
  const navigate = useNavigate();
  const region = useActiveRegion() ?? DEFAULT_REGION;
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const updateAge = () => setNow(new Date());
    const interval = window.setInterval(updateAge, 60_000);
    window.addEventListener('focus', updateAge);
    return () => { window.clearInterval(interval); window.removeEventListener('focus', updateAge); };
  }, []);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    setLoading(true);
    setItems([]);
    setCursor(null);
    let active = true;
    notificationAPI.list().then(({ data }) => {
      if (active) { setItems(data.notifications); setCursor(data.next_cursor); setError(null); void refreshCount(); }
    }).catch(() => { if (active) setError('Notifications are temporarily unavailable. Please try again shortly.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; mounted.current = false; };
  }, [reload, refreshCount]);

  const read = async (item?: NotificationItem, open = false) => {
    setBusy(item ? String(item.id) : 'all'); setError(null);
    try {
      if (item) await notificationAPI.read(item.id);
      else await notificationAPI.readAll();
      if (!mounted.current) return;
      setItems(current => current.map(row => !item || row.id === item.id ? { ...row, is_unread: false, read_at: row.read_at ?? new Date().toISOString() } : row));
      void refreshCount();
      if (open && item?.action_path) navigate(item.action_path);
    } catch { if (mounted.current) setError('Unable to mark notifications as read. Please try again.'); }
    finally { if (mounted.current) setBusy(null); }
  };
  const loadMore = async () => {
    if (!cursor) return;
    setBusy('more'); setError(null);
    try {
      const { data } = await notificationAPI.list(cursor);
      if (!mounted.current) return;
      setItems(current => [...current, ...data.notifications.filter(row => !current.some(existing => existing.id === row.id))]);
      setCursor(data.next_cursor);
    } catch { if (mounted.current) setError('Unable to load more notifications. Please try again.'); }
    finally { if (mounted.current) setBusy(null); }
  };
  const unreadItem = async (item: NotificationItem) => {
    setBusy(String(item.id)); setError(null);
    try {
      await notificationAPI.unread(item.id);
      if (!mounted.current) return;
      setItems(current => current.map(row => row.id === item.id ? { ...row, read_at: null, is_unread: !row.inactive_at } : row));
      void refreshCount();
    } catch { if (mounted.current) setError('Unable to mark this notification as unread. Please try again.'); }
    finally { if (mounted.current) setBusy(null); }
  };
  const unread = items.filter(item => item.is_unread).length;
  if (loading) return <LoadingSpinner message="Loading notifications…" />;
  return (
    <div className="space-y-7">
      <SEO title="Notifications – OppFoundry" description="New matches and saved opportunity reminders." />
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
        <div><h1 className="text-3xl font-semibold text-gray-900">Notifications</h1><p className="mt-3 text-gray-600">New matches and reminders for your saved opportunities.</p></div>
        <Link to="/account#notifications" className="text-sm font-medium text-blue-600 hover:underline sm:pt-2">Notification settings →</Link>
      </div>
      {error && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">{error} {!items.length && <button onClick={() => { setLoading(true); setReload(value => value + 1); }} className="ml-3 font-semibold underline">Try again</button>}</div>}
      {!error && !items.length ? <div className="rounded-2xl border border-gray-200 bg-white px-6 py-14 text-center">
        <BellIcon className="mx-auto h-12 w-12 text-slate-300" /><h2 className="mt-5 text-lg font-semibold text-slate-900">You’re all caught up</h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">New matching opportunities and reminders for saved opportunities will appear here.</p>
        <Link to={buildRegionPath(region, 'saved')} className="mt-6 inline-flex rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-500">View Saved Opportunities</Link>
      </div> : items.length > 0 && <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm" aria-label="Notification history">
        <div className="flex items-center justify-between gap-4 border-b border-gray-200 px-5 py-4 sm:px-6">
          <span className="text-sm text-gray-500">Notification history</span>
          <button disabled={busy !== null} onClick={() => void read()} className="text-sm font-semibold text-blue-600 hover:underline disabled:opacity-50">{busy === 'all' ? 'Marking…' : 'Mark all as read'}</button>
        </div>
        <ul className="divide-y divide-gray-200">{items.map(item => <li key={item.id} className={`flex gap-3 px-5 py-6 sm:gap-4 sm:px-6 ${item.inactive_at ? 'bg-gray-50' : item.is_unread ? 'bg-blue-50/50' : ''}`}>
          <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${item.inactive_at ? 'bg-gray-100 text-gray-400' : 'bg-blue-50 text-blue-600'}`}>{item.event_type === 'new_opportunity_match' ? <BellIcon className="h-5 w-5" /> : <ClockIcon className="h-5 w-5" />}</div>
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center gap-2 text-xs text-gray-500">{item.is_unread && <span aria-label="Unread" className="h-2 w-2 shrink-0 rounded-full bg-blue-600" />}<span>{item.event_type === 'saved_opportunity_deadline' ? 'Deadline reminder' : item.event_type === 'new_opportunity_match' ? 'New match' : 'Update'}{notificationAge(item.created_at, now) && <> · {notificationAge(item.created_at, now)}</>}</span></div>
            <h2 className={`text-base font-semibold ${item.inactive_at ? 'text-gray-500' : 'text-gray-900'}`}>
              {item.action_path && item.inactive_reason !== 'opportunity_removed' ? <Link to={item.action_path} onClick={event => {
                if (!event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && item.is_unread) { event.preventDefault(); if (busy === null) void read(item, true); }
              }} className="hover:text-blue-600 hover:underline">{item.title}</Link> : item.title}
            </h2>
            <p className="mt-2 text-sm text-gray-600">{item.body}</p>
            {item.event_type === 'new_opportunity_match' && <MatchReasons reasons={item.metadata.match_reasons} className="mt-2" />}
            {item.inactive_at && <span className="mt-2 inline-flex rounded bg-gray-100 px-2 py-1 text-xs text-gray-500">{inactiveLabels[item.inactive_reason ?? ''] ?? 'Reminder inactive'}</span>}
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
              {item.is_unread && <button disabled={busy !== null} onClick={() => void read(item)} className="font-semibold text-blue-600 hover:underline disabled:opacity-50">{busy === String(item.id) ? 'Marking…' : 'Mark as read'}</button>}
              {item.read_at && !item.inactive_at && <button disabled={busy !== null} onClick={() => void unreadItem(item)} className="font-semibold text-blue-600 hover:underline disabled:opacity-50">{busy === String(item.id) ? 'Marking…' : 'Mark as unread'}</button>}
            </div>
          </div>
        </li>)}</ul>
        {cursor && <div className="border-t border-gray-200 p-4 text-center"><button disabled={busy !== null} onClick={() => void loadMore()} className="text-sm font-semibold text-blue-600 disabled:opacity-50">{busy === 'more' ? 'Loading…' : 'Load more'}</button></div>}
        <span className="sr-only" aria-live="polite">{unread} unread in loaded notifications</span>
      </section>}
    </div>
  );
};

export default Notifications;
